<template>
  <div class="choice-constraint-editor" @paste="handlePaste">
    <div v-if="constraints.length === 0" class="empty-state">
      <p>Nog geen beperkingen geconfigureerd.</p>
      <Btn variant="dashed" icon="plus-circle" @click="addConstraint" type="button">
        Beperking toevoegen
      </Btn>
    </div>

    <div
      v-for="(constraint, cIdx) in constraints"
      :key="constraint.id"
      class="constraint-block"
      tabindex="-1"
    >
      <div class="constraint-block__header">
        <TextInput
          :id="`constraint_label_${cIdx}`"
          label="Naam"
          v-model="constraint.label"
          placeholder="bijv. Kastmaat beschikbaarheid"
          @input="emitUpdate"
        />
        <Btn variant="danger" size="sm" icon="trash" @click="removeConstraint(cIdx)" type="button" />
      </div>

      <div class="constraint-block__config">
        <SelectInput
          :id="`constraint_target_${cIdx}`"
          label="Doeloptie (wordt beperkt)"
          v-model="constraint.targetOptionId"
          :options="choiceOptionsList"
          placeholder="Kies een keuze-optie..."
          @update:modelValue="emitUpdate"
        />

        <div class="dimension-selectors">
          <SelectInput
            :id="`constraint_dim_row_${cIdx}`"
            label="Y-as (rij dimensie)"
            v-model="constraint.rowOptionId"
            :options="getAvailableDimensionOptions(constraint, 'col')"
            placeholder="bijv. Hoogte"
            @update:modelValue="emitUpdate"
          />
          <SelectInput
            :id="`constraint_dim_col_${cIdx}`"
            label="X-as (kolom dimensie)"
            v-model="constraint.colOptionId"
            :options="getAvailableDimensionOptions(constraint, 'row')"
            placeholder="bijv. Breedte"
            @update:modelValue="emitUpdate"
          />
        </div>

        <div class="constraint-block__actions">
          <Btn
            variant="primary"
            size="sm"
            @click="generateGrid(cIdx)"
            type="button"
            :disabled="!constraint.targetOptionId || !constraint.rowOptionId || !constraint.colOptionId"
            icon="grid"
          >
            Matrix genereren
          </Btn>
          <Btn
            variant="secondary"
            size="sm"
            @click="exportCsv(cIdx)"
            type="button"
            icon="export"
            :disabled="!constraint.grid || constraint.grid.length === 0"
          >
            Export CSV
          </Btn>
          <Btn variant="secondary" size="sm" @click="openCsvImport(cIdx)" type="button" icon="upload">
            Import CSV
          </Btn>
        </div>

        <p class="constraint-block__hint">
          <BaseIcon name="info" size="14" />
          <span>
            CSV-formaat: eerste rij bevat
            <strong>{{ getOptionLabel(constraint.colOptionId) || 'kolom' }}</strong>-waarden,
            eerste kolom bevat
            <strong>{{ getOptionLabel(constraint.rowOptionId) || 'rij' }}</strong>-waarden. Vul per
            cel de toegestane keuzes in, gescheiden door <code>/</code> (bijv.
            <code>Klein/Middel</code>). Lege cel = geen keuzes toegestaan. Je kunt ook direct
            data uit Excel of Google Sheets plakken (Cmd/Ctrl+V): op een cel om een rij of blok
            vanaf die cel te vullen, of buiten de cellen om de hele matrix te vervangen. Een
            maat rondt naar boven af naar de eerstvolgende rij of kolom; een keuze-as gebruikt
            de keuzelabels als rij- of kolomwaarden. Meerdere beperkingen op dezelfde doeloptie
            gelden samen: een keuze moet in elke beperking zijn toegestaan.
          </span>
        </p>
      </div>

      <!-- Grid -->
      <div v-if="constraint.grid && constraint.grid.length > 0" class="constraint-grid">
        <div class="constraint-grid__stats">
          <span>{{ constraint.grid.length }} rijen &times; {{ getColValues(constraint).length }} kolommen</span>
          <span class="constraint-grid__legend">
            <span class="legend-item legend-item--checked"><BaseIcon name="check" size="12" /> Beschikbaar</span>
            <span class="legend-item legend-item--unchecked">Niet beschikbaar</span>
          </span>
        </div>

        <div class="matrix-table-wrapper">
          <table class="matrix-table">
            <thead>
              <tr>
                <th class="row-header">{{ getOptionLabel(constraint.rowOptionId) }}</th>
                <th
                  v-for="colVal in getColValues(constraint)"
                  :key="colVal"
                  class="col-header"
                >
                  {{ formatDimValue(constraint.colOptionId, colVal) }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(row, rowIdx) in constraint.grid" :key="rowIdx">
                <td class="row-cell">{{ formatDimValue(constraint.rowOptionId, row.rowValue) }}</td>
                <td
                  v-for="(cell, colIdx) in row.cells"
                  :key="colIdx"
                  class="choice-cell"
                  tabindex="-1"
                  :data-row="rowIdx"
                  :data-col="colIdx"
                >
                  <div class="choice-cell__choices">
                    <label
                      v-for="choice in getTargetChoices(constraint)"
                      :key="choice.id"
                      class="choice-checkbox"
                      :class="{ 'choice-checkbox--checked': cell.allowedChoiceIds.includes(choice.id) }"
                      :title="choice.label"
                    >
                      <input
                        type="checkbox"
                        :checked="cell.allowedChoiceIds.includes(choice.id)"
                        @change="toggleChoice(cIdx, rowIdx, colIdx, choice.id)"
                      />
                      <span class="choice-checkbox__label">{{ choice.label }}</span>
                    </label>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

    </div>

    <Btn
      v-if="constraints.length > 0"
      variant="dashed"
      icon="plus-circle"
      @click="addConstraint"
      type="button"
      class="mt-4"
    >
      Nog een beperking toevoegen
    </Btn>
  </div>
</template>

<script>
  import { ref, computed, watch } from 'vue';
  import { uid } from 'uid';
  import SelectInput from '@/components/inputs/SelectInput.vue';
  import TextInput from '@/components/inputs/TextInput.vue';
  import Btn from '@/components/partials/Btn.vue';
  import BaseIcon from '@/components/partials/BaseIcon.vue';

  export default {
    name: 'ChoiceConstraintEditor',

    components: {
      SelectInput,
      TextInput,
      Btn,
      BaseIcon,
    },

    props: {
      modelValue: {
        type: Array,
        default: () => [],
      },
      productOptions: {
        type: Array,
        default: () => [],
      },
      computedFields: {
        type: Array,
        default: () => [],
      },
    },

    emits: ['update:modelValue'],

    setup(props, { emit }) {
      const constraints = ref(JSON.parse(JSON.stringify(props.modelValue || [])));

      watch(
        () => props.modelValue,
        (newVal) => {
          const incoming = JSON.stringify(newVal || []);
          const current = JSON.stringify(constraints.value);
          if (incoming !== current) {
            constraints.value = JSON.parse(incoming);
          }
        },
        { deep: true }
      );

      const emitUpdate = () => {
        emit('update:modelValue', JSON.parse(JSON.stringify(constraints.value)));
      };

      const getComputedFields = () => props.computedFields || [];

      const getComputedField = (fieldId) =>
        getComputedFields().find((field) => field?.id === fieldId);

      // Infer numeric range for computed axes from sibling number options
      const getComputedFieldNumberBounds = () => {
        let min = null;
        let max = null;
        let step = null;

        (props.productOptions || []).forEach((option) => {
          if (option?.type !== 'number' || !option.numberConfig) return;
          const optMin = parseFloat(option.numberConfig.min);
          const optMax = parseFloat(option.numberConfig.max);
          const optStep = parseFloat(option.numberConfig.step);
          if (!isNaN(optMin)) min = min == null ? optMin : Math.min(min, optMin);
          if (!isNaN(optMax)) max = max == null ? optMax : Math.max(max, optMax);
          if (!isNaN(optStep) && optStep > 0) {
            step = step == null ? optStep : Math.min(step, optStep);
          }
        });

        return {
          min: min ?? 0,
          max: max ?? 5000,
          step: step ?? 100,
        };
      };

      const choiceOptionsList = computed(() => {
        return props.productOptions
          .filter((o) => o.type === 'single-choice' || o.type === 'multi-choice')
          .map((o) => ({
            value: o.id,
            label: o.label || o.id,
          }));
      });

      const dimensionOptionsList = computed(() => {
        const numberOptions = props.productOptions
          .filter((o) => o.type === 'number')
          .map((o) => ({
            value: o.id,
            label: o.label || o.id,
          }));

        const computed = getComputedFields()
          .filter((field) => field?.id && field?.formula)
          .map((field) => ({
            value: field.id,
            label: `${field.label || field.id} (berekend)`,
          }));

        // A choice option as axis: one row/column per choice, matched on id.
        // Lets a size limit depend on e.g. the motor type.
        const choiceOptions = props.productOptions
          .filter((o) => o.type === 'single-choice')
          .map((o) => ({
            value: o.id,
            label: `${o.label || o.id} (keuze)`,
          }));

        return [...computed, ...numberOptions, ...choiceOptions];
      });

      const getDimensionOption = (optionId) =>
        props.productOptions.find((o) => o.id === optionId);

      const isChoiceDimension = (optionId) => {
        const type = getDimensionOption(optionId)?.type;
        return type === 'single-choice' || type === 'multi-choice';
      };

      const getDimensionChoices = (optionId) =>
        getDimensionOption(optionId)?.choiceConfig?.choices || [];

      // Grid values are choice ids for a choice axis; show the label instead.
      const formatDimValue = (optionId, value) => {
        if (!isChoiceDimension(optionId)) return value;
        const choice = getDimensionChoices(optionId).find((c) => c.id === value);
        return choice?.label || value;
      };

      // Parse a CSV header/row value for an axis: a number for a size axis, a
      // choice id (matched on label or id) for a choice axis. null when invalid.
      const parseDimValue = (optionId, raw) => {
        const text = String(raw ?? '').trim();
        if (!text) return null;
        if (!isChoiceDimension(optionId)) {
          const num = Number(text);
          return Number.isFinite(num) ? num : null;
        }
        const choice = getDimensionChoices(optionId).find(
          (c) => c.id === text || normalizeLabel(c.label) === normalizeLabel(text)
        );
        return choice?.id || null;
      };

      const getAvailableDimensionOptions = (constraint, excludeKey) => {
        const excludeId = excludeKey === 'row' ? constraint.rowOptionId : constraint.colOptionId;
        return dimensionOptionsList.value.filter((o) => o.value !== excludeId);
      };

      const getOptionLabel = (optionId) => {
        const opt = props.productOptions.find((o) => o.id === optionId);
        if (opt?.label) return opt.label;
        const computed = getComputedField(optionId);
        return computed?.label || optionId || '';
      };

      const getTargetChoices = (constraint) => {
        const opt = props.productOptions.find((o) => o.id === constraint.targetOptionId);
        return opt?.choiceConfig?.choices || [];
      };

      const getColValues = (constraint) => {
        if (!constraint.grid || constraint.grid.length === 0) return [];
        return constraint.grid[0].cells.map((c) => c.colValue);
      };

      const getNumberOptionValues = (optionId) => {
        const opt = props.productOptions.find((o) => o.id === optionId);
        if (isChoiceDimension(optionId)) {
          return getDimensionChoices(optionId).map((c) => c.id);
        }
        if (opt?.type === 'number' && opt.numberConfig) {
          const min = parseFloat(opt.numberConfig.min) || 0;
          const max = parseFloat(opt.numberConfig.max) || 100;
          const step = parseFloat(opt.numberConfig.step) || 1;
          const values = [];
          for (let v = min; v <= max; v += step) {
            values.push(v);
          }
          return values;
        }

        if (getComputedField(optionId)) {
          const { min, max, step } = getComputedFieldNumberBounds();
          const values = [];
          for (let v = min; v <= max; v += step) {
            values.push(v);
          }
          return values;
        }

        return [];
      };

      const addConstraint = () => {
        constraints.value.push({
          id: `constraint_${uid()}`,
          label: '',
          targetOptionId: '',
          rowOptionId: '',
          colOptionId: '',
          grid: [],
        });
        emitUpdate();
      };

      const removeConstraint = (idx) => {
        constraints.value.splice(idx, 1);
        emitUpdate();
      };

      const generateGrid = (cIdx) => {
        const constraint = constraints.value[cIdx];
        const rowValues = getNumberOptionValues(constraint.rowOptionId);
        const colValues = getNumberOptionValues(constraint.colOptionId);
        const choices = getTargetChoices(constraint);
        const allChoiceIds = choices.map((c) => c.id);

        constraint.grid = rowValues.map((rowVal) => ({
          rowValue: rowVal,
          cells: colValues.map((colVal) => ({
            colValue: colVal,
            allowedChoiceIds: [...allChoiceIds],
          })),
        }));

        emitUpdate();
      };

      const toggleChoice = (cIdx, rowIdx, colIdx, choiceId) => {
        const cell = constraints.value[cIdx].grid[rowIdx].cells[colIdx];
        const idx = cell.allowedChoiceIds.indexOf(choiceId);
        if (idx >= 0) {
          cell.allowedChoiceIds.splice(idx, 1);
        } else {
          cell.allowedChoiceIds.push(choiceId);
        }
        emitUpdate();
      };

      const openCsvImport = (cIdx) => {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = '.csv';
        input.onchange = (e) => {
          const file = e.target.files[0];
          if (file) {
            importCsvFile(cIdx, file);
          }
        };
        input.click();
      };

      const csvEscape = (val) => {
        const s = String(val ?? '');
        if (/[",\n;]/.test(s)) {
          return `"${s.replace(/"/g, '""')}"`;
        }
        return s;
      };

      const slugify = (val) => {
        return String(val || 'beperking')
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/^-+|-+$/g, '')
          .slice(0, 40) || 'beperking';
      };

      const exportCsv = (cIdx) => {
        const constraint = constraints.value[cIdx];
        if (!constraint?.grid?.length) return;

        const choices = getTargetChoices(constraint);
        const labelById = new Map(choices.map((c) => [c.id, c.label]));
        const colValues = getColValues(constraint);

        const rowHeader = getOptionLabel(constraint.rowOptionId);
        const colHeader = getOptionLabel(constraint.colOptionId);
        const cornerLabel = rowHeader && colHeader ? `${rowHeader} \\ ${colHeader}` : '';

        const lines = [];
        lines.push(
          [cornerLabel, ...colValues.map((v) => formatDimValue(constraint.colOptionId, v))]
            .map(csvEscape)
            .join(',')
        );

        constraint.grid.forEach((row) => {
          const cells = row.cells.map((cell) => {
            const labels = (cell.allowedChoiceIds || [])
              .map((id) => labelById.get(id))
              .filter(Boolean);
            return labels.join('/');
          });
          lines.push(
            [formatDimValue(constraint.rowOptionId, row.rowValue), ...cells]
              .map(csvEscape)
              .join(',')
          );
        });

        const csv = '\uFEFF' + lines.join('\n');
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `optiebeperking-${slugify(constraint.label)}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      };

      // Detecteer het scheidingsteken. Kopiëren vanuit Excel/Sheets geeft
      // tab-gescheiden tekst; Excel-NL exporteert CSV's met ';'.
      const detectDelimiter = (headerLine) => {
        if (headerLine.includes('\t')) return '\t';
        const semi = (headerLine.match(/;/g) || []).length;
        const comma = (headerLine.match(/,/g) || []).length;
        return semi > comma ? ';' : ',';
      };

      // Vergelijk keuzelabels ongevoelig voor hoofdletters en spaties.
      const normalizeLabel = (s) => String(s ?? '').trim().toLowerCase();

      // Zet celinhoud als "Klein/Middel" om naar de bijbehorende choice-ids.
      const parseCellChoices = (constraint, content) => {
        const byLabel = new Map(
          getTargetChoices(constraint).map((c) => [normalizeLabel(c.label), c.id])
        );
        return String(content || '')
          .split('/')
          .map((s) => byLabel.get(normalizeLabel(s)))
          .filter(Boolean);
      };

      // Verwijder eventuele omringende quotes van een CSV-cel.
      const unquoteCell = (raw) => {
        const s = String(raw ?? '').trim();
        if (s.startsWith('"') && s.endsWith('"')) {
          return s.slice(1, -1).replace(/""/g, '"');
        }
        return s;
      };

      const importCsvText = (cIdx, text) => {
        const constraint = constraints.value[cIdx];
        const choices = getTargetChoices(constraint);

        const rawLines = text.replace(/^\uFEFF/, '').trim().split(/\r?\n/);
        if (rawLines.length < 2) {
          alert('CSV bestand is leeg of heeft te weinig rijen');
          return;
        }

        const delimiter = detectDelimiter(rawLines[0]);
        // Verwijder lege cellen aan het einde van elke regel — Excel kopieert
        // vaak extra lege kolommen mee.
        const splitLine = (line) => {
          const cells = line.split(delimiter).map(unquoteCell);
          while (cells.length && !cells[cells.length - 1].trim()) cells.pop();
          return cells;
        };
        const lines = rawLines.map(splitLine);

        // Kolomwaarden: alle numerieke cellen uit de kopregel, in volgorde.
        // Hoekcellen (leeg of tekst zoals "Hoogte \ Breedte") vallen zo
        // automatisch af. Ook een kop die één kolom naar links verschoven staat
        // (komt vaak voor in Excel-sheets, zonder hoekcel) blijft goed
        // uitgelijnd, omdat we op volgorde koppelen i.p.v. op celpositie.
        const headerRow = lines[0];
        const colValues = headerRow
          .map((v) => parseDimValue(constraint.colOptionId, v))
          .filter((v) => v !== null);

        if (colValues.length === 0) {
          alert(
            isChoiceDimension(constraint.colOptionId)
              ? 'Geen bekende keuzelabels gevonden in de eerste rij.'
              : 'Geen numerieke kolomwaarden gevonden in de eerste rij.'
          );
          return;
        }

        const choiceIdByLabel = new Map(choices.map((c) => [normalizeLabel(c.label), c.id]));

        const grid = [];
        for (let r = 1; r < lines.length; r++) {
          const row = lines[r];
          let rowValueIdx = 0;
          while (rowValueIdx < row.length && !row[rowValueIdx].trim()) {
            rowValueIdx++;
          }
          const rowValue = parseDimValue(constraint.rowOptionId, row[rowValueIdx]);
          if (rowValue === null) continue;

          // Datacellen beginnen direct ná de rijwaarde — onafhankelijk van hoe
          // de kopregel is uitgelijnd.
          const dataCells = row.slice(rowValueIdx + 1);
          const cells = colValues.map((colVal, ci) => {
            const cellContent = (dataCells[ci] || '').trim();
            let allowedChoiceIds = [];

            if (cellContent) {
              allowedChoiceIds = cellContent
                .split('/')
                .map((s) => choiceIdByLabel.get(normalizeLabel(s)))
                .filter(Boolean);
            }

            return { colValue: colVal, allowedChoiceIds };
          });

          grid.push({ rowValue, cells });
        }

        if (grid.length > 0) {
          constraint.grid = grid;
          emitUpdate();
          alert(`${grid.length} rijen geïmporteerd`);
        } else {
          alert('Geen geldige rijen gevonden in het CSV bestand');
        }
      };

      const importCsvFile = async (cIdx, file) => {
        importCsvText(cIdx, await file.text());
      };

      // Bepaal bij welke beperking een element hoort. Staat het buiten een
      // blok maar is er precies één beperking, dan gebruiken we die.
      const getConstraintIdxFromElement = (el) => {
        const block = el?.closest?.('.constraint-block');
        if (block) {
          const blocks = Array.from(
            block.parentElement?.querySelectorAll?.(':scope > .constraint-block') || []
          );
          return blocks.indexOf(block);
        }
        return constraints.value.length === 1 ? 0 : -1;
      };

      // Parse geplakte data als blok celinhouden (bijv. "Klein/Middel" per cel)
      // voor plakken vanaf een specifieke cel. Tab of ';' als scheidingsteken;
      // zonder scheidingsteken telt elke regel als één cel.
      const parseChoiceBlock = (text) => {
        const lines = text.split(/\r?\n/).filter((l) => l.trim() !== '');
        if (lines.length === 0) return null;
        const delimiter = lines.some((l) => l.includes('\t'))
          ? '\t'
          : lines.some((l) => l.includes(';'))
            ? ';'
            : null;
        return lines.map((l) => (delimiter ? l.split(delimiter) : [l]).map(unquoteCell));
      };

      // Plakken (Cmd/Ctrl+V) van CSV of Excel/Sheets-data direct in een
      // beperking. De geplakte cellen bevatten de toegestane keuzes per cel,
      // gescheiden door '/' (zelfde formaat als de CSV import/export).
      const handlePaste = (event) => {
        const text = event.clipboardData?.getData('text/plain') || '';

        // Plakken in een cel van de matrix: vul een blok vanaf die cel (één
        // rij, een paar cellen of meerdere rijen — zonder kop- en rijwaarden).
        const cellTd = event.target?.closest?.('td.choice-cell');
        if (cellTd && cellTd.dataset?.row != null) {
          const cIdx = getConstraintIdxFromElement(cellTd);
          const constraint = constraints.value[cIdx];
          if (!constraint?.grid?.length) return;
          const cellBlock = parseChoiceBlock(text);
          if (!cellBlock) return;

          event.preventDefault();
          const startRow = Number(cellTd.dataset.row);
          const startCol = Number(cellTd.dataset.col);
          cellBlock.forEach((cells, r) => {
            const gridRow = constraint.grid[startRow + r];
            if (!gridRow) return;
            cells.forEach((content, c) => {
              const cell = gridRow.cells[startCol + c];
              if (!cell) return;
              cell.allowedChoiceIds = parseCellChoices(constraint, content);
            });
          });
          emitUpdate();
          return;
        }

        // Alleen ingrijpen bij tabel-achtige data (meerdere regels met een
        // scheidingsteken); gewone tekst moet normaal in inputs geplakt worden.
        const lines = text.split(/\r?\n/).filter((l) => l.trim());
        if (lines.length < 2 || !/[\t;,]/.test(lines[0])) return;

        const cIdx = getConstraintIdxFromElement(event.target);
        if (cIdx < 0 || !constraints.value[cIdx]) return;

        const constraint = constraints.value[cIdx];
        if (!constraint.targetOptionId || !constraint.rowOptionId || !constraint.colOptionId) {
          alert('Selecteer eerst de doeloptie en beide dimensies voordat je data plakt.');
          return;
        }

        event.preventDefault();
        importCsvText(cIdx, text);
      };

      return {
        constraints,
        choiceOptionsList,
        getAvailableDimensionOptions,
        getOptionLabel,
        getTargetChoices,
        getColValues,
        formatDimValue,
        addConstraint,
        removeConstraint,
        generateGrid,
        toggleChoice,
        openCsvImport,
        exportCsv,
        handlePaste,
        emitUpdate,
      };
    },
  };
</script>

<style scoped lang="scss">
  .choice-constraint-editor {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
  }

  .empty-state {
    text-align: center;
    padding: 2rem;
    color: var(--slate-500);

    p {
      margin-bottom: 1rem;
    }
  }

  .constraint-block {
    border: 1px solid var(--slate-200);
    border-radius: 8px;
    padding: 1rem;
    background: var(--slate-50);
    outline: none;

    &__header {
      display: flex;
      align-items: flex-end;
      gap: 0.75rem;
      margin-bottom: 1rem;

      > :first-child {
        flex: 1;
      }
    }

    &__config {
      display: flex;
      flex-direction: column;
      gap: 0.75rem;
    }

    &__actions {
      display: flex;
      flex-wrap: wrap;
      gap: 0.5rem;
      margin-top: 0.5rem;
    }

    &__hint {
      display: flex;
      align-items: flex-start;
      gap: 0.5rem;
      margin-top: 0.5rem;
      padding: 0.5rem 0.75rem;
      border-radius: 6px;
      background: var(--brand-50, var(--slate-100));
      color: var(--slate-600);
      font-size: 0.75rem;
      line-height: 1.4;

      :deep(svg) {
        flex-shrink: 0;
        margin-top: 1px;
        color: var(--brand-500, var(--slate-500));
      }

      strong {
        color: var(--slate-800);
        font-weight: 600;
      }

      code {
        background: var(--slate-200);
        color: var(--slate-800);
        padding: 0 0.25rem;
        border-radius: 3px;
        font-family: monospace;
        font-size: 0.7rem;
      }
    }
  }

  .dimension-selectors {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 0.75rem;
  }

  .constraint-grid {
    margin-top: 1rem;

    &__stats {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 0.5rem;
      font-size: 0.8125rem;
      color: var(--slate-500);
    }

    &__legend {
      display: flex;
      gap: 1rem;
    }
  }

  .legend-item {
    display: flex;
    align-items: center;
    gap: 0.25rem;
    font-size: 0.75rem;

    &--checked {
      color: var(--green-600);
    }

    &--unchecked {
      color: var(--slate-400);
    }
  }

  .matrix-table-wrapper {
    overflow-x: auto;
    border: 1px solid var(--slate-200);
    border-radius: 6px;
    background: white;
  }

  .matrix-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 0.6875rem;

    th, td {
      padding: 0.25rem 0.375rem;
      border: 1px solid var(--slate-200);
      white-space: nowrap;
    }

    th {
      background: var(--slate-100);
      font-weight: 600;
      color: var(--slate-700);
      text-align: center;
    }

    .row-header {
      text-align: left;
      min-width: 60px;
    }

    .row-cell {
      font-weight: 600;
      background: var(--slate-50);
      text-align: right;
      padding-right: 0.5rem;
    }
  }

  .choice-cell {
    min-width: 44px;
    vertical-align: top;
    outline: none;
  }

  .choice-cell__choices {
    display: flex;
    flex-direction: column;
    gap: 0;
  }

  .choice-checkbox {
    display: flex;
    align-items: center;
    gap: 0.25rem;
    cursor: pointer;
    padding: 0 2px;
    border-radius: 3px;
    transition: background 0.15s;

    @include hover {
      background: var(--slate-100);
    }

    input[type='checkbox'] {
      width: 12px;
      height: 12px;
      cursor: pointer;
      accent-color: var(--brand-500);
    }

    &__label {
      font-size: 0.625rem;
      color: var(--slate-600);
      @include ellipsis(1);
      max-width: 56px;
    }

    &--checked {
      .choice-checkbox__label {
        color: var(--slate-800);
        font-weight: 500;
      }
    }
  }

  .mt-4 {
    margin-top: 1rem;
  }
</style>
